import type { TFunction } from 'i18next';
import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { listCards, listVehicles, type ApiOutcome, type Card, type Vehicle } from './api';
import type { MethodPick } from './form-model';
import { NativeSelect } from './NativeSelect';

type List<T> =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly items: readonly T[] }
  | { readonly status: 'unavailable' };

/** One of the owner's lists, loaded again when the owner changes. */
function useOwnerList<T>(
  load: (owner?: string) => Promise<ApiOutcome<readonly T[]>>,
  owner: string | undefined,
): List<T> {
  const [list, setList] = useState<List<T>>({ status: 'loading' });
  useEffect(() => {
    let active = true;
    setList({ status: 'loading' });
    void load(owner).then((outcome) => {
      if (!active) return;
      setList(outcome.ok ? { status: 'ready', items: outcome.value } : { status: 'unavailable' });
    });
    return () => {
      active = false;
    };
  }, [load, owner]);
  return list;
}

interface Choice {
  readonly id: string;
  readonly label: string;
}

function hintFor(
  t: TFunction<'expenses'>,
  status: List<unknown>['status'],
  choiceCount: number,
  emptyText: string,
): string | null {
  if (status === 'loading') return t('capture.references.loading');
  if (status === 'unavailable') return t('capture.references.unavailable');
  return choiceCount === 0 ? emptyText : null;
}

/**
 * A select of the owner's list with «Non in elenco» first (always allowed). A stored choice the list
 * no longer holds (or a list that did not load) still shows, under its stored label.
 */
function OwnerListSelect({
  fieldKey,
  list,
  choices,
  value,
  storedLabel,
  emptyText,
  labelText,
  onChange,
}: {
  readonly fieldKey: 'cardId' | 'vehicleId';
  /** Overrides the field's own label («Conto» for a transfer's account). */
  readonly labelText?: string;
  readonly list: List<unknown>;
  readonly choices: readonly Choice[];
  readonly value: string;
  readonly storedLabel: string;
  readonly emptyText: string;
  readonly onChange: (id: string, label: string) => void;
}) {
  const { t } = useTranslation('expenses');
  const uid = useId();
  const options =
    value !== '' && !choices.some((c) => c.id === value)
      ? [...choices, { id: value, label: storedLabel || value }]
      : choices;
  const hint = hintFor(t, list.status, choices.length, emptyText);
  return (
    <div className="min-w-0 space-y-1" data-field={fieldKey}>
      <label htmlFor={`${uid}-${fieldKey}`} className="text-sm font-medium">
        {labelText ?? t(`capture.fields.${fieldKey}`)}
      </label>
      <NativeSelect
        id={`${uid}-${fieldKey}`}
        value={value}
        aria-describedby={hint ? `${uid}-${fieldKey}-hint` : undefined}
        onChange={(e) => {
          const id = e.target.value;
          onChange(id, options.find((o) => o.id === id)?.label ?? '');
        }}
      >
        <option value="">{t('capture.references.notListed')}</option>
        {options.map((option) => (
          <option key={option.id} value={option.id}>
            {option.label}
          </option>
        ))}
      </NativeSelect>
      {hint && (
        <p id={`${uid}-${fieldKey}-hint`} className="text-xs text-muted-foreground">
          {hint}
        </p>
      )}
    </div>
  );
}

/**
 * «Carta» or «Conto»: the owner's payment methods of the class and scope «Come hai pagato?» names
 * (decision 153). nexpense's transfers, cash and other entries are never offered as a card.
 */
export function CardPicker({
  owner,
  pick,
  value,
  storedLabel,
  onChange,
}: {
  /** The expense's owner; undefined for the caller. */
  readonly owner: string | undefined;
  readonly pick: MethodPick;
  readonly value: string;
  readonly storedLabel: string;
  readonly onChange: (id: string, label: string) => void;
}) {
  const { t } = useTranslation('expenses');
  const list = useOwnerList<Card>(listCards, owner);
  const transfer = pick.methodClass === 'transfer';
  const choices =
    list.status === 'ready'
      ? list.items
          .filter(
            (card) =>
              card.methodClass === pick.methodClass &&
              (pick.scope === 'any' || card.kind === pick.scope),
          )
          .map((card) => ({ id: card.id, label: card.label }))
      : [];
  return (
    <OwnerListSelect
      fieldKey="cardId"
      list={list}
      choices={choices}
      value={value}
      storedLabel={storedLabel}
      emptyText={t(transfer ? 'capture.references.emptyAccounts' : 'capture.references.emptyCards')}
      {...(transfer ? { labelText: t('capture.fields.accountId') } : {})}
      onChange={onChange}
    />
  );
}

/** «Veicolo»: the owner's vehicles, for mileage and fuel. */
export function VehiclePicker({
  owner,
  value,
  storedLabel,
  onChange,
}: {
  readonly owner: string | undefined;
  readonly value: string;
  readonly storedLabel: string;
  readonly onChange: (id: string, label: string) => void;
}) {
  const { t } = useTranslation('expenses');
  const list = useOwnerList<Vehicle>(listVehicles, owner);
  const choices =
    list.status === 'ready'
      ? list.items.map((vehicle) => ({
          id: vehicle.id,
          label: vehicle.plate
            ? t('capture.references.plate', { label: vehicle.label, plate: vehicle.plate })
            : vehicle.label,
        }))
      : [];
  return (
    <OwnerListSelect
      fieldKey="vehicleId"
      list={list}
      choices={choices}
      value={value}
      storedLabel={storedLabel}
      emptyText={t('capture.references.emptyVehicles')}
      onChange={onChange}
    />
  );
}
